import React, { useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../features/authentication/hooks/useAuth';
import { useRoleAccess } from '../features/users/hooks/useRoleAccess';
import Sidebar from './Sidebar';
import BottomNavigation from './BottomNavigation';
import { AlertCircle, Menu } from 'lucide-react';
import { SidebarProvider, useSidebar } from '../contexts/SidebarContext';
import { RequestDataProvider } from '../features/requests/context/RequestsContext';
import { isHotwireNative } from '../lib/native';
import { usePageMeta } from '../hooks/usePageMeta';

interface LayoutProps {
  children: React.ReactNode;
}

function LayoutContent({ children }: LayoutProps) {
  const { toggleSidebar, isMobile } = useSidebar();
  const navRef = useRef<HTMLDivElement>(null);
  const isNative = isHotwireNative();
  const pageMeta = usePageMeta();
  
  // Update document title and navigation bar when route changes
  useEffect(() => {
    const pageTitle = pageMeta.title || 'Go Sit Back Admin';
    
    // Set document title for browser tab
    document.title = pageTitle;
    
    // Update native iOS navigation bar
    if (isNative && navRef.current) {
      navRef.current.setAttribute('data-navigation-title', pageTitle);
      
      // Trigger the bridge component's update by dispatching a custom event
      // that the Stimulus controller can listen to
      const updateEvent = new CustomEvent('navigation:update', { 
        bubbles: true,
        detail: { title: pageTitle }
      });
      navRef.current.dispatchEvent(updateEvent);
    }
  }, [pageMeta.title, isNative]);
  
  // Handle native navigation button taps
  useEffect(() => {
    if (!isNative) return;
    
    const handleMenuButton = () => {
      console.log('📱 Native menu button event received');
      toggleSidebar();
    };
    
    const handleProfileButton = () => {
      console.log('📱 Native profile button event received');
      window.location.href = '/profile';
    };
    
    const handleSearchButton = () => {
      console.log('📱 Native search button event received');
      // Add search functionality here
    };
    
    // Listen on window instead of a specific element for more reliability
    window.addEventListener('native-menu-button', handleMenuButton);
    window.addEventListener('native-profile-button', handleProfileButton);
    window.addEventListener('native-search-button', handleSearchButton);
    
    return () => {
      window.removeEventListener('native-menu-button', handleMenuButton);
      window.removeEventListener('native-profile-button', handleProfileButton);
      window.removeEventListener('native-search-button', handleSearchButton);
    };
  }, [isNative, toggleSidebar]);

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Native navigation controller */}
      {isNative && (
        <div
          ref={navRef}
          data-controller="navigation"
          data-navigation-title={pageMeta.title || 'Go Sit Back Admin'}
          data-navigation-show-menu="true"
          data-navigation-show-search="false"
          data-navigation-show-profile="true"
          style={{ display: 'none' }}
        />
      )}
      
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile header with menu toggle - hide in native iOS */}
        {isMobile && !isNative && (
          <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between md:hidden">
            <button
              onClick={toggleSidebar}
              className="p-2 rounded-md text-gray-600 hover:bg-gray-100 touch-target"
              aria-label="Toggle menu"
            >
              <Menu className="h-6 w-6" />
            </button>
            <div className="w-10" /> {/* Spacer */}
          </div>
        )}
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50 p-4 sm:p-6 pb-20 md:pb-6">
          {children}
        </main>
        {/* Bottom Navigation - Mobile Only */}
        {isMobile && <BottomNavigation />}
      </div>
    </div>
  );
}

export default function Layout({ children }: LayoutProps) {
  const { isAuthenticated, loading: authLoading, user, logout } = useAuth();
  const permissions = useRoleAccess();

  // Add debug logging in development
  if (import.meta.env.DEV) {
    console.log('Layout render - authLoading:', authLoading, 'isAuthenticated:', isAuthenticated, 'user:', user ? 'present' : 'none');
  }

  // Show loading spinner while authentication state is being determined
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Checking authentication...</p>
          {/* Add debug info in development */}
          {import.meta.env.DEV && (
            <p className="mt-2 text-xs text-gray-400">
              Auth loading: {String(authLoading)}, User: {user ? 'Present' : 'None'}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check if user has any permissions to access the staff interface
  const hasStaffAccess = permissions.canViewDashboard || 
                        permissions.canViewCustomers || 
                        permissions.canViewJobs || 
                        permissions.canViewSchedule;

  if (!hasStaffAccess) {
    
        const handleLogout = async () => {
          try {
            await logout();
          } catch (error) {
            console.error('Logout failed:', error);
          }
        };    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
          <AlertCircle className="h-12 w-12 text-orange-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Restricted</h2>
          <p className="text-gray-600 mb-4">You don't have permission to access the staff interface.</p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
            <h3 className="font-medium text-blue-900 mb-2">Available Options:</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• <a href="/client-portal" className="underline">Access Client Portal</a></li>
              <li>• <a href="/book-service" className="underline">Book a Service</a></li>
              <li>• Contact your administrator for staff access</li>
            </ul>
          </div>
          <div className="mt-6 flex flex-col space-y-2">
            <button
              onClick={handleLogout}
              className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Sign Out
            </button>
          </div>
          <div className="mt-4 text-xs text-gray-500">
            Current role: {user?.role || 'unknown'} | User: {user?.email || 'unknown'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <RequestDataProvider>
        <LayoutContent>{children}</LayoutContent>
      </RequestDataProvider>
    </SidebarProvider>
  );
}